import {Component, Inject, Prop, Vue} from "vue-property-decorator";
import {getClientSubscriptionDate} from "~/modules/client/service";
import {Getter} from "vuex-class";
import {Loader} from "~/components/loader";
import {Icon, icons} from "~/components/icon";
import {Columns} from "~/components/grid/columns";
import {Column} from "~/components/grid/column";
import {fileSize, formatDate, intConverter} from "~/lib/helpers/formatters";
import {className} from "~/lib/helpers/string";
import {Label} from "~/components/forms/label";
import {Client} from "~/lib/models/client";
import {TimeRange} from "~/lib/time";
import {AppSettings} from "~/lib/app-settings";

type Quota = {
  key: string;
  max: number;
  usage: number;
  isReached: boolean;
  start?: Date,
  end?: Date;
  valueTransform?: Function
}

const quotasLabels = {
  maps: "Nombre de cartes",
  datasets: "Nombre de tables",
  analysis: "Nombre d'analyses",
  dbStorage: "Capacité de stockage des tables",
  geocoding: "Lignes géocodées",
  users: "Nombre d'utilisateurs",
  sharedLinks: "Nombre de partages externes",
  mediasStorage: "Capacité de stockage des médias",
  footfallAddress : "Flux piéton: Adresses",
  footfallCredits: "Flux piéton: Crédits",
};

const enginesNames = {
  prdt: "Portrait robot du territoire",
  implanting: "Implantation idéale",
  simouv: "Simulation Ouverture",
  reporting: "Reporting commercial",
};

const quotasValueTransform = {
  dbStorage: v => fileSize(v),
  mediasStorage: v => fileSize(v),
  geocoding: v => intConverter()(v)
};

const quotasListToDisplay = [
  "users",
  "maps",
  "datasets",
  "analysis",
  "dbStorage",
  "geocoding",
  "sharedLinks",
  "mediasStorage",
  "footfallAddress",
  "footfallCredits"
];

@Component
export class ClientSubscription extends Vue {

  @Inject() appSettings: AppSettings;
  @Prop({type: Object, required: true}) client: Client;
  @Getter currentUser;

  private quotas = null;
  private packs = null;
  private isLoading = false;

  get subscriptionRange() {
    if (this.client.subscription.duration) {
      return TimeRange.createFromStartDate(
        this.client.subscription.startDate,
        this.client.subscription.duration)
    }
  }

  async created() {
    this.isLoading = true;
    let data = await getClientSubscriptionDate(this.appSettings.clientUUID);
    this.quotas = data.quotas;
    this.packs = data.packs;
    this.isLoading = false;
  }

  renderQuotaComponent(key) {
    if (this.quotas[key] && quotasLabels[key]) {
      if (quotasValueTransform[key]) {
        this.quotas[key].valueTransform = quotasValueTransform[key];
      }
      return this.quotas[key].max === -1 ?
        <QuotaComponentInfinite label={quotasLabels[key]} quota={this.quotas[key]}/> :
        <QuotaComponent label={quotasLabels[key]} quota={this.quotas[key]}/>;
    }
  }

  renderPacks() {
    return this.packs.map(pack => {
      return <div>{pack.label}</div>;
    });

  }

  renderEngines() {

    let engines = this.$functionalities("engines");

    if (!engines) {
      return <p>Aucun outil disponible</p>
    }

    return Object.keys(engines)
      .filter(key => engines[key].enabled)
      .map(key => {
        let name = engines[key]?.label || enginesNames[key];
        if (!name) return;  // Silently exclude non declared engines
        return (
          <div class={className("engine")}>
            <div>{name}</div>
          </div>
        );
    });
  }

  render() {

    if (this.isLoading) return <Loader/>;

    return (
      <div class="subscription">

        {this.client.subscription?.startDate && (
          <Columns>
            <Column class="subscription-dates">
              <div>
                <Label text="Début de contrat"/>
                <div>{formatDate(this.client.subscription.startDate, "dd.MM.yyyy")}</div>
              </div>
              {this.client.subscription.duration && (
                <div>
                  <Label text="Fin de contrat"/>
                  <div>{formatDate(this.subscriptionRange.end, "dd.MM.yyyy")}</div>
                </div>
              )}
            </Column>
          </Columns>
        )}

        {this.client.subscription?.offerLabel && (
          <Columns>
            <Column>
              <h2 class="page-subtitle">Formule</h2>
              <div class="list">
                <div>
                  <div>{this.client.subscription.offerLabel}</div>
                </div>
              </div>
            </Column>
          </Columns>
        )}

        <Columns>
          <Column>
            <h2 class="page-subtitle">Outils sélectionnés</h2>
            <div class="list subscription__list">
              {this.renderEngines()}
            </div>
          </Column>
          <Column>
            <h2 class="page-subtitle">Données disponibles</h2>
            <div class="list subscription__list">
              {this.renderPacks()}
            </div>
          </Column>
        </Columns>


        <h2 class="page-subtitle">Usages</h2>
        {this.$functionalities("quotas").mode === 0 ?
          <p>Usages à l'utilisateur, relevés client indisponibles</p> :
          <Columns class="is-multiline">
            {quotasListToDisplay.map(key => <Column class="is-4">{this.renderQuotaComponent(key)} </Column>)}
          </Columns>
        }

      </div>
    );
  }
}

@Component
class QuotaComponent extends Vue {

  @Prop({type: String, required: true}) label: string;
  @Prop({type: Object, required: true}) quota: Quota;

  markProgressFull() {
    return this.quota.usage >= this.quota.max;
  }

  value(v) {
    return this.quota.valueTransform ? this.quota.valueTransform(v) : v;
  }

  render() {
    return (
      <div class="quota">
        <h3 class="label">
          {this.label}
          {this.quota.end && (
            <span class="end-date">
              Remise à 0 le {formatDate(this.quota.end, "dd.MM.yyyy")}
            </span>
          )}
        </h3>
        <div class="quota_progress">
          <Progress total={this.quota.max} current={this.quota.usage} isDanger={this.markProgressFull()}/>
          <div class="count">{this.value(this.quota.usage)} / {this.value(this.quota.max)}</div>
        </div>
      </div>
    );
  }

}

@Component
class QuotaComponentInfinite extends Vue {

  @Prop({type: String, required: true}) label: string;
  @Prop({type: Object, required: true}) quota: Quota;

  value(v) {
    return this.quota.valueTransform ? this.quota.valueTransform(v) : v;
  }

  render() {
    return (
      <div class="quota quota--infinite">
        <h3 class="label">{this.label}</h3>
        <div class="quota_progress">
          <Icon icon={icons.infinity}/>
          <div class="count">
            {this.value(this.quota.usage)}</div>
        </div>
      </div>
    );
  }

}

@Component
class Progress extends Vue {

  @Prop({type: Number, required: true}) total: number;
  @Prop({type: Number, required: true}) current: number;
  @Prop({type: Boolean, required: false}) isDanger: boolean;

  get perc() {
    if (this.total === 0) return 0;
    return Math.floor(this.current / this.total * 100);
  }

  get style() {
    return {
      width: `${this.perc}%`
    }
  }

  render() {
    return (
      <div class={this.isDanger ? "progress progress--danger" : "progress"}>
        <div class="inner" style={this.style}/>
      </div>
    );
  }

}

// VUE JSX HOT LOADER //
if (module.hot) require("/usr/src/app/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-7a97f5d3/subscription.tsx" });